"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useMutation } from "convex/react";
import { useAuth } from "@clerk/clerk-react";
import { api } from "../../../../convex/_generated/api";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  PluginIcon,
  UploadIcon,
  FileIcon,
  XIcon,
  ArrowRightIcon,
  TagIcon,
} from "@/components/icons";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function UploadPluginPage() {
  const router = useRouter();
  const { isSignedIn, isLoaded } = useAuth();

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [version, setVersion] = useState("1.0.0");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const generateUploadUrl = useMutation(api.plugins.generateUploadUrl);
  const createPlugin = useMutation(api.plugins.create);

  // Redirect if not signed in
  if (isLoaded && !isSignedIn) {
    router.push("/login?redirect=/plugins/upload");
    return null;
  }

  const handleFileSelect = (selectedFile: File) => {
    setError(null);

    if (!selectedFile.name.toLowerCase().endsWith(".jar")) {
      setError("Only .jar files are allowed");
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError("File too large. Maximum size is 10MB.");
      return;
    }

    setFile(selectedFile);

    // Auto-fill name from filename if empty
    if (!name) {
      const baseName = selectedFile.name.replace(/\.jar$/i, "");
      setName(baseName.replace(/[-_]/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleAddTag = () => {
    const normalizedTag = tagInput.toLowerCase().trim();
    if (normalizedTag && !tags.includes(normalizedTag) && tags.length < 10) {
      setTags([...tags, normalizedTag]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!file) {
      setError("Please select a file to upload");
      return;
    }

    if (!name.trim()) {
      setError("Please enter a plugin name");
      return;
    }

    if (!shortDescription.trim()) {
      setError("Please enter a short description");
      return;
    }

    if (!description.trim()) {
      setError("Please enter a full description");
      return;
    }

    if (!version.trim()) {
      setError("Please enter a version number");
      return;
    }

    try {
      setIsUploading(true);

      // Get upload URL
      const uploadUrl = await generateUploadUrl();

      // Upload file to Convex storage
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type || "application/java-archive" },
        body: file,
      });

      if (!response.ok) {
        throw new Error("Failed to upload file");
      }

      const { storageId } = await response.json();

      // Create plugin record
      const result = await createPlugin({
        name: name.trim(),
        description: description.trim(),
        shortDescription: shortDescription.trim(),
        version: version.trim(),
        fileId: storageId,
        fileName: file.name,
        fileSize: file.size,
        tags,
      });

      // Redirect to the new plugin page
      router.push(`/plugins/${result.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload plugin");
      setIsUploading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1">
        <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-3 text-primary">
              <UploadIcon className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Upload Plugin
              </h1>
              <p className="text-muted">
                Share your plugin with the community
              </p>
            </div>
          </div>

          {/* Upload Form */}
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            {/* File Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileIcon className="h-5 w-5" />
                  Plugin File
                </CardTitle>
              </CardHeader>
              <CardContent>
                {file ? (
                  <div className="flex items-center justify-between rounded-lg border border-border bg-foreground/5 p-4">
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-primary/10 p-2 text-primary">
                        <PluginIcon className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {file.name}
                        </p>
                        <p className="text-sm text-muted">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setFile(null)}
                    >
                      <XIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
                      dragOver
                        ? "border-primary bg-primary/5"
                        : "border-border"
                    }`}
                  >
                    <UploadIcon className="h-12 w-12 text-muted" />
                    <p className="mt-4 text-center text-foreground">
                      Drag and drop your .jar file here
                    </p>
                    <p className="mt-1 text-sm text-muted">
                      or click to browse (max 10MB)
                    </p>
                    <input
                      type="file"
                      accept=".jar"
                      onChange={(e) =>
                        e.target.files?.[0] && handleFileSelect(e.target.files[0])
                      }
                      className="absolute inset-0 cursor-pointer opacity-0"
                    />
                    <Button type="button" variant="outline" className="mt-4">
                      Browse Files
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Plugin Details */}
            <Card>
              <CardHeader>
                <CardTitle>Plugin Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-foreground">
                    Plugin Name *
                  </label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="My Awesome Plugin"
                    maxLength={100}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-foreground">
                    Version *
                  </label>
                  <Input
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder="1.0.0"
                    maxLength={20}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-foreground">
                    Short Description * (displayed in cards)
                  </label>
                  <Input
                    value={shortDescription}
                    onChange={(e) => setShortDescription(e.target.value)}
                    placeholder="A brief one-line description of your plugin"
                    maxLength={150}
                  />
                  <p className="mt-1 text-xs text-muted">
                    {shortDescription.length}/150 characters
                  </p>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-foreground">
                    Full Description *
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what your plugin does, how to use it, and any important information..."
                    rows={6}
                    maxLength={5000}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="mt-1 text-xs text-muted">
                    {description.length}/5000 characters — Supports Markdown: **bold**, *italic*, `code`, lists, headers, and more.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Tags */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TagIcon className="h-5 w-5" />
                  Tags (optional)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Add a tag..."
                    maxLength={30}
                  />
                  <Button type="button" variant="outline" onClick={handleAddTag}>
                    Add
                  </Button>
                </div>
                {tags.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="rounded-full p-0.5 hover:bg-primary/20"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-xs text-muted">
                  Add up to 10 tags to help users find your plugin
                </p>
              </CardContent>
            </Card>

            {/* Error Message */}
            {error && (
              <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-4 text-sm text-red-500">
                {error}
              </div>
            )}

            {/* Submit */}
            <div className="flex items-center justify-between">
              <Link href="/plugins">
                <Button type="button" variant="outline">
                  <ArrowRightIcon className="mr-2 h-4 w-4 rotate-180" />
                  Cancel
                </Button>
              </Link>
              <Button type="submit" disabled={isUploading}>
                {isUploading ? (
                  <>Uploading...</>
                ) : (
                  <>
                    <UploadIcon className="mr-2 h-4 w-4" />
                    Upload Plugin
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
}
